import type { FC, HTMLAttributes, ReactNode } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import GameIconsCrystalBars from '~icons/game-icons/crystal-bars'
import IcBaselineCircle from '~icons/ic/baseline-circle'
import LucideMousePointerClick from '~icons/lucide/mouse-pointer-click'
import nasukeSpinner from 'src/assets/nasuke-spinner.png'
import whiteMioSpinner from 'src/assets/white-mio-spinner.png'
import { MioExpression, MioImage } from 'src/components/Common/MioImage'
import { TASK_PRIORITIES } from 'src/components/GenerationTask/constants'
import { Preloader } from 'src/components/Preloader/Preloader'
import { hash } from 'src/utils/hash'
import { cx } from '../../utils/cx'
import { UISpinnerLoading } from '../ui/SpinnerLoading'
import styles from './TaskPlaceholderImage.module.css'

function getMioSpinClass(status: string, priority: number): string {
  if (status === 'running') return 'animate-spin-running'
  if (priority >= TASK_PRIORITIES.default) return 'animate-spin-fast'
  if (priority >= TASK_PRIORITIES.turboMode) return 'animate-spin-medium'
  return 'animate-spin-slow'
}

const AnimatedText: FC<{ text: string }> = ({ text }) => {
  const letters = useMemo(() => text.split(''), [text])
  return (
    <div className="tracking-[0.1rem] uppercase font-light">
      {letters.map((letter, i) => (
        <span
          key={i}
          className={cx(styles['animated-text-letter'])}
          style={{
            animationDelay: `${i * 30}ms`,
          }}
        >
          {letter}
        </span>
      ))}
    </div>
  )
}

interface TaskPlaceholderImageProps extends HTMLAttributes<HTMLDivElement> {
  id?: string
  status?: string
  waitingDescription?: ReactNode
  withDescription?: boolean
  icon?: ReactNode
  defaultClassName?: string
  placeholderVariant?: 'default' | 'dark-purple'
  contentStyleProps?: StyleProps
  /** When set, shows a spinning Mio mascot instead of the default spinner. Spin speed varies by priority. */
  mioSpinnerPriority?: number
  /** When true (and a mio spinner is already being shown), swap the Mio mascot for Nasuke. */
  useNasukeSpinner?: boolean
}

const TaskPlaceholderImage: FC<TaskPlaceholderImageProps> = ({
  id = '',
  status,
  className,
  waitingDescription,
  withDescription = true,
  icon,
  children,
  style,
  defaultClassName,
  placeholderVariant = 'default',
  contentStyleProps,
  mioSpinnerPriority,
  useNasukeSpinner = false,
  ...props
}) => {
  const { t } = useTranslation(['generation'])

  const rootClassName =
    defaultClassName ?? styles[`${placeholderVariant}-placeholder`]

  const indicator = useMemo((): {
    icon: ReactNode
    description: ReactNode
    /** Whether background animation should be paused */
    pauseAnimation?: boolean
    /** Whether to use the stopped-placeholder style (failed/cancelled) */
    stopped?: boolean
  } => {
    if (status === 'failed' || status === 'cancelled') {
      const isFailed = status === 'failed'
      return {
        icon: (
          <MioImage
            expression={isFailed ? MioExpression.Cry : MioExpression.Sulk}
            className={styles['stopped-mio']}
          />
        ),
        description: (
          <div className={styles['stopped-card']}>
            {isFailed ? (
              <>
                <p className="text-[13px] leading-relaxed text-white/60 text-center">
                  {t('generation:generation.credits.failed-apology')}
                </p>
                <p className="text-[13px] leading-relaxed text-white/90 text-center font-medium flex items-center gap-1.5">
                  <GameIconsCrystalBars className="size-3.5 text-amber-400 shrink-0" />
                  {t('generation:generation.credits.failed-refund')}
                </p>
              </>
            ) : (
              <p className="text-[13px] leading-relaxed text-white/60 text-center capitalize">
                {status}
              </p>
            )}
            <div className="text-[10px] text-white/25 font-mono mt-0.5">
              {id}
            </div>
          </div>
        ),
        stopped: true,
      }
    }

    if (status === 'awaiting-choice') {
      return {
        icon: <LucideMousePointerClick className="size-7" />,
        description: (
          <AnimatedText
            text={t(
              'generation:model-experiment.placeholder.cta',
              'Choose your favorite',
            )}
          />
        ),
      }
    }

    if (status === 'waiting' || status === 'running') {
      const mioIcon =
        mioSpinnerPriority != null ? (
          <img
            src={useNasukeSpinner ? nasukeSpinner : whiteMioSpinner}
            alt=""
            aria-hidden
            className={cx(
              'size-16',
              getMioSpinClass(status, mioSpinnerPriority),
            )}
            style={{ rotate: `${Math.random() * 360}deg` }}
          />
        ) : null

      if (status === 'waiting') {
        return {
          icon: mioIcon ?? (
            <UISpinnerLoading loading className="text-white size-7" />
          ),
          description: (
            <>
              <div className="capitalize">{status}...</div>
              {waitingDescription && (
                <p className="px-4">{waitingDescription}</p>
              )}
            </>
          ),
        }
      }

      return {
        icon: mioIcon ?? <Preloader dark inline duration={1500} />,
        description: <AnimatedText text="Generating" />,
      }
    }

    return {
      icon: <IcBaselineCircle className="size-6" />,
      description: (
        <div className="capitalize">
          {`${status === 'completed' ? '' : 'Unknown:'}`} {status}
        </div>
      ),
      pauseAnimation: true,
    }
  }, [id, status, waitingDescription, t, mioSpinnerPriority, useNasukeSpinner])

  return (
    <div
      className={cx(
        className,
        indicator.stopped ? styles['stopped-placeholder'] : rootClassName,
      )}
      style={{
        ...(indicator.stopped
          ? {}
          : {
              '--placeholder-delay': `-${hash(id) % 15}s`,
              '--placeholder-play-state': indicator.pauseAnimation
                ? 'paused'
                : 'running',
            }),
        ...style,
      }}
      {...props}
    >
      <div
        className={cx(
          'w-full h-full flex flex-col justify-center items-center relative',
          indicator.stopped ? 'gap-2' : 'gap-4',
          contentStyleProps?.className,
        )}
        style={contentStyleProps?.style}
      >
        {icon ?? indicator.icon}
        {withDescription && indicator.description}
        {children}
      </div>
    </div>
  )
}

export default TaskPlaceholderImage
